import { Box, Button, Grid, Link, Skeleton, Stack, Tooltip, Typography } from '@mui/material'
import { t } from 'i18next'
import React, { useCallback, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getPresignedUrl } from '../../../../api/export/batchExport'
import { useNotification } from '../../../../components/Notification/NotificationContext'

import {
	detailsLabelStyle,
	detailsValueStyle,
	dialogButtonStyle,
	iconStyle,
	linkStyle,
	detailsCampaignMessageStyle,
} from './DetailsHeader.styles'
import { DownloadCloud, PlusSquare, RefreshCw, Trash2 } from 'react-feather'
import { useTheme } from '@mui/material/styles'
import ConfirmationDialog from '../../../../components/ConfirmationDialog/ConfirmationDialog'
import { Trans } from 'react-i18next'
import { useAppDispatch } from '../../../../store/utils/hooks'
import { actionSaveImportStep } from '../../../../store/slices/import/importSlice'
import { ImportStep } from '../../../../store/slices/import/types/types'
import useUsageReport from '../../../../api/quota/useUsageReport'
import EditCampaignName from '../EditCampaignName/EditCampaignName'
import { CREATE_CAMPAIGN_PATH } from '../../../../routes/ImportRoutes'
import { CAMPAIGN_MANAGER_PATH } from '../../../../routes/CampaignManagerRoutes'
import { actionDeleteCampaign } from '../../../../store/slices/campaign/campaignSlice'

type DetailsHeaderProps = {
	openCampaignDialog: () => void
	disabled: boolean
	campaign: any
}
export default function DetailsHeader({ openCampaignDialog, disabled = false, campaign }: DetailsHeaderProps) {
	const dispatch = useAppDispatch()
	const { campaignId } = useParams()
	const { palette } = useTheme()
	const navigate = useNavigate()
	const { addMessage } = useNotification()
	const [showConfirmationDialog, setShowConfirmationDialog] = useState(false)
	const { usage } = useUsageReport()
	const [lockDelete, setLockDelete] = useState(false)

	const exportLeads = useCallback(async () => {
		try {
			if (campaignId) {
				addMessage({ success: 'Your file is being prepared.', timeout: 2000 })
				const { url } = await getPresignedUrl(campaignId)
				const link = document.createElement('a')
				link.href = url
				link.download = 'export.csv'
				document.body.appendChild(link)
				link.click()
				document.body.removeChild(link)
			}
		} catch (err) {
			addMessage({ error: 'Error creating file.' })
		}
	}, [addMessage, campaignId])

	const addMoreLeads = useCallback(() => {
		dispatch(actionSaveImportStep(ImportStep.UPLOAD_LEADS))
		navigate(CREATE_CAMPAIGN_PATH)
	}, [navigate, dispatch])

	const handleDeleteCampaign = useCallback(async () => {
		setLockDelete(true)
		const result = await dispatch(actionDeleteCampaign(campaignId as string))
		if (result.meta.requestStatus === 'fulfilled') {
			navigate(CAMPAIGN_MANAGER_PATH)
		}
		setLockDelete(false)
	}, [campaignId, dispatch, navigate])

	const deleteCampaignPrompt = useCallback(() => setShowConfirmationDialog(true), [])

	const closeDialog = useCallback(() => setShowConfirmationDialog(false), [setShowConfirmationDialog])

	const getCampaignMessage = () => {
		if (
			campaign?.campaign_state?.code === 'error' &&
			campaign.campaign_state.message.includes('call quota limits')
		) {
			const hasAvailableCalls =
				usage && usage.daily_calls < usage.max_daily_calls && usage.total_calls < usage.max_total_calls

			if (hasAvailableCalls) {
				return (
					<span>
						{t('campaign_details.call_limit_message')}{' '}
						<Link
							component="span"
							onClick={openCampaignDialog}
							sx={{ cursor: 'pointer', color: palette.primary.main }}
						>
							{t('campaign_details.resume_calling')}
						</Link>
					</span>
				)
			}
			return campaign.campaign_state.message
		}
		return campaign?.status
	}
	const getStatusMessage = () => {
		if (
			campaign?.campaign_state?.code === 'error' &&
			campaign.campaign_state.message.includes('call quota limits')
		) {
			return 'Stopped'
		}
		return campaign?.status
	}

	return (
		<>
			<Stack>
				<Grid container>
					<Grid item>
						<Typography variant="h1" sx={{ color: palette.text.primary, fontSize: '32px' }}>
							{t('campaign_details.title')}
						</Typography>
					</Grid>
					<Grid item sx={{ marginLeft: 'auto' }}>
						<Tooltip arrow placement="top" title={t('campaign_details.call_prospects_i')}>
							<Button
								variant="outlined"
								onClick={openCampaignDialog}
								disabled={disabled}
								sx={dialogButtonStyle}
							>
								<RefreshCw style={iconStyle} />
								<span style={{ marginLeft: '8px' }}>
									<Trans i18nKey="campaign_details.call_prospects" />
								</span>
							</Button>
						</Tooltip>
					</Grid>
				</Grid>
				<Stack sx={{ marginTop: '10px' }}>
					<Box sx={{ height: '30px' }} display="flex">
						<Typography sx={detailsLabelStyle(palette)}>{t('campaign_details.name_label')}</Typography>
						<EditCampaignName campaign={campaign} sx={detailsValueStyle(palette)} />
					</Box>
					<Box sx={{ height: '30px' }} display="flex">
						<Typography sx={detailsLabelStyle(palette)}>{t('campaign_details.status_label')}</Typography>
						{getStatusMessage() ? (
							<Typography sx={detailsValueStyle(palette)}>{getStatusMessage()}</Typography>
						) : (
							<Skeleton sx={{ width: '25%' }} />
						)}
					</Box>
					<Box sx={{ height: '30px' }} display="flex">
						<Typography sx={detailsLabelStyle(palette)}>
							{t('campaign_details.total_times_run_label')}
						</Typography>
						{campaign?.campaign_status ? (
							<Typography sx={detailsValueStyle(palette)}>{campaign.campaign_status}</Typography>
						) : (
							<Skeleton sx={{ width: '25%' }} />
						)}
					</Box>
					{campaign?.campaign_state?.code === 'error' &&
						campaign.campaign_state.message.includes('call quota limits') && (
							<Box sx={{ height: '30px' }} display="flex">
								<Typography sx={detailsLabelStyle(palette)}>
									{t('campaign_details.campaign_message')}
								</Typography>

								<Typography sx={detailsCampaignMessageStyle(palette)}>
									{getCampaignMessage()}
								</Typography>
							</Box>
						)}
				</Stack>
				<Box sx={{ margin: '16px 0' }}>
					<Link sx={linkStyle} onClick={exportLeads}>
						<DownloadCloud style={iconStyle} />
						<span>{t('campaign_details.export_link_label')}</span>
					</Link>
					<Link sx={linkStyle} onClick={addMoreLeads}>
						<PlusSquare style={iconStyle} />
						<span>{t('campaign_details.add_more_leads_label')}</span>
					</Link>
					<Link sx={linkStyle} onClick={deleteCampaignPrompt}>
						<Trash2 style={iconStyle} />
						<span>{t('campaign_details.delete_campaign_label')}</span>
					</Link>
				</Box>
			</Stack>

			<ConfirmationDialog
				open={showConfirmationDialog}
				onClose={closeDialog}
				action={handleDeleteCampaign}
				acceptDisabled={lockDelete}
				i18nPrimaryLabel="campaign_details.delete_campaign_go_back"
				i18nSecondaryLabel="campaign_details.delete_campaign_confirmation"
				i18nDescription="campaign_details.delete_campaign_confirmation_message"
				i18nTitle="campaign_details.delete_campaign_confirmation_title"
			/>
		</>
	)
}
